'use client';

import type { PredictionSide } from '@flipster/apis/v1/price-prediction';
import { FormattedMessage } from '@flipster/i18n';
import { Badge } from '@flipster/react/design-system/badge';
import Formatted from '@flipster/react/design-utils/formatted';
import ArrowDownRightIcon from '@flipster/react/icon/generated/ArrowDownRightIcon';
import ArrowUpRightIcon from '@flipster/react/icon/generated/ArrowUpRightIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, vstack } from '@flipster/styled-system/patterns';
import { UsdtPrecision } from '@flipster/utils/constants/currency';

import { HSeparator, VSeparator } from '../milestone/misc/separator';

interface MyStatusCardProps {
  priceDirection: PredictionSide | undefined;
  volume: string;
  expectedReward: {
    ifUpWins: string;
    ifDownWins: string;
  };
}

export const MyStatusCard = ({ priceDirection, volume, expectedReward }: MyStatusCardProps) => {
  const isVoted = !!priceDirection;

  return (
    <div
      className={css({
        w: '100%',
        px: { base: 16, tablet: 40 },
        py: { base: 16, tablet: 32 },
        gap: { base: 16, tablet: 24 },
        rounded: 16,
        bg: 'background.default.level-1',
        display: 'flex',
        flexDirection: { base: 'column', tablet: 'row' },
        alignItems: 'center',
      })}
    >
      {/* My Price Direction */}
      <div className={vstack({ gap: 8, alignItems: 'center', flex: 1, w: { base: '100%', tablet: 'auto' } })}>
        <span className={css({ textStyle: 'Title/S/Regular', color: 'content.default.level-2' })}>
          <FormattedMessage id='Campaign.r260123.My_price_prediction' />
        </span>
        {isVoted ? (
          <div className={hstack({ gap: 4, alignItems: 'center' })}>
            {priceDirection === 'UP' ? (
              <ArrowUpRightIcon size={16} className={css({ color: 'content.state.positive' })} />
            ) : (
              <ArrowDownRightIcon size={16} className={css({ color: 'content.state.negative' })} />
            )}
            <span
              className={css({
                textStyle: 'Monospace/Title/XS',
                color: priceDirection === 'UP' ? 'content.state.positive' : 'content.state.negative',
              })}
            >
              {priceDirection === 'UP' ? (
                <FormattedMessage id='Campaign.r260123.Going_up_non_capital' />
              ) : (
                <FormattedMessage id='Campaign.r260123.Going_down_non_capital' />
              )}
            </span>
          </div>
        ) : (
          <span className={css({ textStyle: 'Monospace/Title/XS', color: 'content.default.level-5' })}>
            <FormattedMessage id='Campaign.r260123.Not_voted' />
          </span>
        )}
      </div>

      <HSeparator css={{ display: { base: 'block', tablet: 'none' }, w: '100%' }} />
      <VSeparator css={{ display: { base: 'none', tablet: 'block' }, alignSelf: 'stretch' }} />

      {/* My Volume */}
      <div className={vstack({ gap: 8, alignItems: 'center', flex: 1, w: { base: '100%', tablet: 'auto' } })}>
        <span className={css({ textStyle: 'Title/S/Regular', color: 'content.default.level-2' })}>
          <FormattedMessage id='Campaign.r260123.My_daily_volume_usdt' />
        </span>
        <span className={css({ textStyle: 'Monospace/Title/XS', color: 'content.default.level-5' })}>
          <Formatted.Number value={volume} />
        </span>
      </div>

      <HSeparator css={{ display: { base: 'block', tablet: 'none' }, w: '100%' }} />
      <VSeparator css={{ display: { base: 'none', tablet: 'block' }, alignSelf: 'stretch' }} />

      {/* My Expected Reward */}
      <div className={vstack({ gap: 8, alignItems: 'center', flex: 1, w: { base: '100%', tablet: 'auto' } })}>
        <span className={css({ textStyle: 'Title/S/Regular', color: 'content.default.level-2' })}>
          <FormattedMessage id='Campaign.r260123.My_expected_reward_if_correct_usdt' />
        </span>
        {isVoted ? (
          <Formatted.Number
            value={priceDirection === 'UP' ? expectedReward.ifUpWins : expectedReward.ifDownWins}
            withPlus
            withColor
            toFixed
            precision={UsdtPrecision}
            css={{ textStyle: 'Monospace/Title/XS' }}
          />
        ) : (
          <div className={hstack({ gap: 12, alignItems: 'center' })}>
            <div className={hstack({ gap: 8, alignItems: 'center' })}>
              <Badge type='tint' color='positive'>
                <FormattedMessage id='Campaign.r260123.Label_up' />
              </Badge>
              <Formatted.Number
                value={expectedReward.ifUpWins}
                withPlus
                withColor
                toFixed
                precision={UsdtPrecision}
                css={{ textStyle: 'Monospace/Title/XS' }}
              />
            </div>
            <div className={css({ w: '1px', h: '19px', bg: 'background.default.level-4' })} />
            <div className={hstack({ gap: 8, alignItems: 'center' })}>
              <Badge type='tint' color='negative'>
                <FormattedMessage id='Campaign.r260123.Label_down' />
              </Badge>
              <Formatted.Number
                value={expectedReward.ifDownWins}
                withPlus
                withColor
                toFixed
                precision={UsdtPrecision}
                css={{ textStyle: 'Monospace/Title/XS' }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
